<app-card [loading]="!!(pageLoading$ | async)">
  <div class="header">
    <div class="filters">
      <form class="flex" [formGroup]="statisticsForm">
        <app-input-wrapper
          [control]="statisticsForm.controls['cistern']"
          isSelect
        >
          <select formControlName="cistern">
            @for (
              cistern of cisternOptions$ | async;
              track "sc_" + cistern.label
            ) {
              <option [value]="cistern.value">{{ cistern.label }}</option>
            }
          </select>
        </app-input-wrapper>

        <app-input-wrapper
          [control]="statisticsForm.controls['viewMode']"
          isSelect
        >
          <select formControlName="viewMode">
            @for (viewMode of viewModeOptions; track viewMode.value) {
              <option [value]="viewMode.value">
                {{ viewMode.labelKey | translate }}
              </option>
            }
          </select>
        </app-input-wrapper>

        @if (isCompareMode) {
          <app-input-wrapper
            isSelect
            [control]="statisticsForm.controls['compareChartMode']"
          >
            <select formControlName="compareChartMode">
              @for (
                compareChartMode of compareChartModeOptions;
                track compareChartMode.value
              ) {
                <option [value]="compareChartMode.value">
                  {{ compareChartMode.labelKey | translate }}
                </option>
              }
            </select>
          </app-input-wrapper>
        }

        <app-input-wrapper
          isSelect
          [control]="statisticsForm.controls['period']"
        >
          <select formControlName="period">
            @for (period of periodOptions; track period.value) {
              <option [value]="period.value">
                {{ period.labelKey | translate }}
              </option>
            }
          </select>
        </app-input-wrapper>
      </form>
    </div>

    <button
      type="button"
      class="button icon-button"
      (click)="toggleChartView()"
    >
      @if (isChartView) {
        <app-icon name="ic_tiles"></app-icon>
        {{ "default.view.table" | translate }}
      } @else {
        <app-icon name="ic_analyse"></app-icon>
        {{ "default.view.chart" | translate }}
      }
    </button>
  </div>

  <div class="grid custom-grid flex-1" [class.compare-grid]="isCompareMode">
    <div class="content">
      @if (isCompareMode) {
        @if (comparePanels$ | async; as comparePanels) {
          @if (isChartView) {
            <mat-tab-group
              class="chart-tabs"
              [preserveContent]="false"
              [selectedIndex]="selectedChartTab === 'level' ? 0 : 1"
              (selectedIndexChange)="onChartTabChange($event)"
            >
              <mat-tab [label]="'statistics.chartTabs.level' | translate">
                <ng-template matTabContent>
                  <div class="compare-content">
                    <section class="compare-panel">
                      <ng-container
                        *ngTemplateOutlet="
                          periodHeader;
                          context: {
                            titleKey: getPeriodTitleKey('primary'),
                            form: primaryPeriodForm,
                          }
                        "
                      />
                      @if (!isCompareOverlapMode) {
                        @if (
                          comparePanels.primary.waterLevelChartData.length >
                            0 && hasWaterLevelData(comparePanels.primary.rows)
                        ) {
                          <app-chart-water-level
                            [sensorData]="
                              comparePanels.primary.waterLevelChartData
                            "
                            [interval]="comparePanels.primary.interval"
                          />
                        } @else {
                          <p>{{ "statistics.empty" | translate }}</p>
                        }
                      }
                    </section>

                    <section class="compare-panel">
                      <ng-container
                        *ngTemplateOutlet="
                          periodHeader;
                          context: {
                            titleKey: getPeriodTitleKey('secondary'),
                            form: secondaryPeriodForm,
                          }
                        "
                      />
                      @if (!isCompareOverlapMode) {
                        @if (
                          comparePanels.secondary.waterLevelChartData.length >
                            0 && hasWaterLevelData(comparePanels.secondary.rows)
                        ) {
                          <app-chart-water-level
                            [sensorData]="
                              comparePanels.secondary.waterLevelChartData
                            "
                            [interval]="comparePanels.secondary.interval"
                          />
                        } @else {
                          <p>{{ "statistics.empty" | translate }}</p>
                        }
                      }
                    </section>

                    @if (isCompareOverlapMode) {
                      <section class="compare-panel full-panel">
                        @if (
                          compareOverlapWaterLevelChartData$ | async;
                          as overlapWaterLevelChartData
                        ) {
                          @if (
                            overlapWaterLevelChartData.length > 0 &&
                            (hasCompareOverlapWaterLevelData$ | async)
                          ) {
                            <app-chart-water-level
                              [sensorData]="overlapWaterLevelChartData"
                              [overlap]="true"
                              [interval]="
                                (compareOverlapInterval$ | async) ?? null
                              "
                            />
                          } @else {
                            <p>{{ "statistics.empty" | translate }}</p>
                          }
                        }
                      </section>
                    }
                  </div>
                </ng-template>
              </mat-tab>

              <mat-tab [label]="'statistics.chartTabs.delta' | translate">
                <ng-template matTabContent>
                  <div class="compare-content">
                    <section class="compare-panel">
                      <ng-container
                        *ngTemplateOutlet="
                          periodHeader;
                          context: {
                            titleKey: getPeriodTitleKey('primary'),
                            form: primaryPeriodForm,
                          }
                        "
                      />
                      @if (!isCompareOverlapMode) {
                        @if (
                          comparePanels.primary.deltaChartData.length > 0 &&
                          hasDeltaData(comparePanels.primary.rows)
                        ) {
                          <app-chart-sensor-data
                            [sensorData]="comparePanels.primary.deltaChartData"
                            [interval]="comparePanels.primary.interval"
                          />
                        } @else {
                          <p>{{ "statistics.empty" | translate }}</p>
                        }
                      }
                    </section>

                    <section class="compare-panel">
                      <ng-container
                        *ngTemplateOutlet="
                          periodHeader;
                          context: {
                            titleKey: getPeriodTitleKey('secondary'),
                            form: secondaryPeriodForm,
                          }
                        "
                      />
                      @if (!isCompareOverlapMode) {
                        @if (
                          comparePanels.secondary.deltaChartData.length > 0 &&
                          hasDeltaData(comparePanels.secondary.rows)
                        ) {
                          <app-chart-sensor-data
                            [sensorData]="
                              comparePanels.secondary.deltaChartData
                            "
                            [interval]="comparePanels.secondary.interval"
                          />
                        } @else {
                          <p>{{ "statistics.empty" | translate }}</p>
                        }
                      }
                    </section>

                    @if (isCompareOverlapMode) {
                      <section class="compare-panel full-panel">
                        @if (
                          compareOverlapDeltaChartData$ | async;
                          as overlapDeltaChartData
                        ) {
                          @if (
                            overlapDeltaChartData.length > 0 &&
                            (hasCompareOverlapDeltaData$ | async)
                          ) {
                            <app-chart-sensor-data
                              [sensorData]="overlapDeltaChartData"
                              [overlap]="true"
                              [interval]="
                                (compareOverlapInterval$ | async) ?? null
                              "
                            />
                          } @else {
                            <p>{{ "statistics.empty" | translate }}</p>
                          }
                        }
                      </section>
                    }
                  </div>
                </ng-template>
              </mat-tab>
            </mat-tab-group>
          } @else {
            <div class="compare-content">
              <section class="compare-panel">
                <ng-container
                  *ngTemplateOutlet="
                    periodHeader;
                    context: {
                      titleKey: getPeriodTitleKey('primary'),
                      form: primaryPeriodForm,
                    }
                  "
                />

                <ng-container
                  *ngTemplateOutlet="
                    statisticsTable;
                    context: { rows: comparePanels.primary.rows }
                  "
                />
              </section>

              <section class="compare-panel">
                <ng-container
                  *ngTemplateOutlet="
                    periodHeader;
                    context: {
                      titleKey: getPeriodTitleKey('secondary'),
                      form: secondaryPeriodForm,
                    }
                  "
                />

                <ng-container
                  *ngTemplateOutlet="
                    statisticsTable;
                    context: { rows: comparePanels.secondary.rows }
                  "
                />
              </section>
            </div>
          }
        }
      } @else {
        @if (isChartView) {
          <mat-tab-group
            class="chart-tabs"
            [preserveContent]="false"
            [selectedIndex]="selectedChartTab === 'level' ? 0 : 1"
            (selectedIndexChange)="onChartTabChangeLevelFirst($event)"
          >
            <mat-tab [label]="'statistics.chartTabs.level' | translate">
              <ng-template matTabContent>
                <div class="compare-content one-panel">
                  <section class="compare-panel">
                    <ng-container
                      *ngTemplateOutlet="
                        periodHeader;
                        context: {
                          titleKey: 'statistics.period.label',
                          form: primaryPeriodForm,
                        }
                      "
                    />

                    @if (waterLevelChartData$ | async; as waterLevelChartData) {
                      @if (
                        !(loading$ | async) &&
                        waterLevelChartData.length > 0 &&
                        (hasWaterLevelData$ | async)
                      ) {
                        <app-chart-water-level
                          [sensorData]="waterLevelChartData"
                          [interval]="(chartInterval$ | async) ?? null"
                        />
                      } @else if (!(loading$ | async)) {
                        <p>{{ "statistics.empty" | translate }}</p>
                      }
                    }
                  </section>
                </div>
              </ng-template>
            </mat-tab>

            <mat-tab [label]="'statistics.chartTabs.delta' | translate">
              <ng-template matTabContent>
                <div class="compare-content one-panel">
                  <section class="compare-panel">
                    <ng-container
                      *ngTemplateOutlet="
                        periodHeader;
                        context: {
                          titleKey: 'statistics.period.label',
                          form: primaryPeriodForm,
                        }
                      "
                    />

                    @if (deltaChartData$ | async; as deltaChartData) {
                      @if (
                        !(loading$ | async) &&
                        deltaChartData.length > 0 &&
                        (hasDeltaData$ | async)
                      ) {
                        <app-chart-sensor-data
                          [sensorData]="deltaChartData"
                          [interval]="(chartInterval$ | async) ?? null"
                        />
                      } @else if (!(loading$ | async)) {
                        <p>{{ "statistics.empty" | translate }}</p>
                      }
                    }
                  </section>
                </div>
              </ng-template>
            </mat-tab>
          </mat-tab-group>
        } @else {
          @if (sensorData$ | async; as sensorData) {
            <section class="compare-panel">
              <ng-container
                *ngTemplateOutlet="
                  periodHeader;
                  context: {
                    titleKey: 'statistics.period.label',
                    form: primaryPeriodForm,
                  }
                "
              />

              <ng-container
                *ngTemplateOutlet="
                  statisticsTable;
                  context: { rows: sensorData }
                "
              />
            </section>
          }
        }
      }
    </div>

    @if (!isCompareMode) {
      @if (cistern$ | async; as cistern) {
        <app-cistern-item-card class="cistern-wrapper" [cistern]="cistern" />
      }
    }
  </div>
</app-card>

<ng-template #periodHeader let-titleKey="titleKey" let-form="form">
  <header class="panel-header">
    <div [formGroup]="form">
      <app-statistics-period-input
        [label]="titleKey | translate"
        [control]="form.controls['date']"
        [period]="statisticsForm.controls['period'].value"
      />
    </div>
  </header>
</ng-template>

<ng-template #statisticsTable let-rows="rows">
  @if (rows.length > 0) {
    <table class="statistics-table">
      <tr>
        <th></th>
        @for (serie of rows[0].series; track serie.name) {
          <th>{{ serie.name }}</th>
        }
      </tr>
      @for (data of rows; track data.timestamp) {
        <tr>
          <th>{{ data.name }}</th>
          @for (serie of data.series; track serie.name) {
            <td>
              {{
                serie.value === null
                  ? "–"
                  : serie.unit === "%"
                    ? (serie.value / 100 | localizedPercent)
                    : (serie.value | localizedNumber) + " " + serie.unit
              }}
            </td>
          }
        </tr>
      }
    </table>
  } @else {
    <p>{{ "statistics.empty" | translate }}</p>
  }
</ng-template>
